import React, { useState, useEffect } from 'react';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { getApiUrl } from '../utils/auth';

const InvestorSetupPassword = () => {
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkStatus, setLinkStatus] = useState(null); // null = checking, 'valid' | 'expired' | 'invalid'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || '';
    setToken(t);
    if (!t) {
      setLinkStatus('invalid');
      return;
    }
    const validate = async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/investor-auth/validate-setup-token?token=${encodeURIComponent(t)}`);
        const result = await res.json();
        if (result.success && result.data) {
          if (result.data.valid) setLinkStatus('valid');
          else if (result.data.expired) setLinkStatus('expired');
          else setLinkStatus('invalid');
        } else {
          setLinkStatus('invalid');
        }
      } catch (_) {
        setLinkStatus('valid'); // show form if validation fails (e.g. network)
      }
    };
    validate();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!token) {
      setError('Invalid link. Please use the link sent to you.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/investor-auth/setup-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.message || 'Failed to set password.');
      }
    } catch (err) {
      console.error('Setup password error:', err);
      setError('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen neo-page flex items-center justify-center p-4 transition-colors duration-200">
        <div className="w-full max-w-md">
          <div className="neo-raised p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mb-4">
              <CheckCircle size={24} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-2">Password set successfully</h2>
            <p className="text-gray-600 dark:text-dark-muted mb-6">You can now log in with your email and this password.</p>
            <a
              href="/"
              className="inline-block px-6 py-2 neo-btn-primary"
            >
              Go to login
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Checking link (validating token)
  if (linkStatus === null) {
    return (
      <div className="min-h-screen neo-page flex items-center justify-center p-4 transition-colors duration-200">
        <div className="w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent mx-auto mb-4" />
          <p className="text-gray-600 dark:text-dark-muted">Checking link...</p>
        </div>
      </div>
    );
  }

  // Invalid or missing link
  if (linkStatus === 'invalid') {
    return (
      <div className="min-h-screen neo-page flex items-center justify-center p-4 transition-colors duration-200">
        <div className="w-full max-w-md">
          <div className="neo-raised p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mb-4">
              <AlertCircle size={24} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-2">Invalid or missing link</h2>
            <p className="text-gray-600 dark:text-dark-muted mb-6">Please use the password setup link that was sent to you.</p>
            <a href="/" className="inline-block px-6 py-2 neo-btn-primary">
              Go to login
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Expired link
  if (linkStatus === 'expired') {
    return (
      <div className="min-h-screen neo-page flex items-center justify-center p-4 transition-colors duration-200">
        <div className="w-full max-w-md">
          <div className="neo-raised p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 mb-4">
              <AlertCircle size={24} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-2">This link has expired</h2>
            <p className="text-gray-600 dark:text-dark-muted mb-4">
              Password setup links are valid for 24 hours. To get a new link, please contact your administrator or the person who invited you.
            </p>
            <p className="text-sm text-gray-500 dark:text-dark-muted mb-6">
              They can send you a new link from Investor Management (View your profile → Copy setup link).
            </p>
            <a href="/" className="inline-block px-6 py-2 neo-btn-primary">
              Go to login
            </a>
          </div>
        </div>
      </div>
    );
  }

  // linkStatus === 'valid' but no token (edge case)
  if (!token) {
    return null;
  }

  return (
    <div className="min-h-screen neo-page flex items-center justify-center p-4 transition-colors duration-200">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-1">Set your password</h1>
          <p className="text-gray-600 dark:text-dark-muted">Enter a new password for your investor account.</p>
        </div>

        <div className="neo-raised p-8 transition-colors duration-200">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">New password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-muted" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  className="w-full pl-10 pr-4 py-2.5 neo-field dark:text-dark-text"
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">Confirm new password *</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-dark-muted" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  className="w-full pl-10 pr-4 py-2.5 neo-field dark:text-dark-text"
                  placeholder="Re-enter password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 neo-btn-primary font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Setting password...' : 'Set password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvestorSetupPassword;
